import { REGULATORY_BASE } from "@/apps/regulatory/paths.js";

export type DocumentViewerContext =
  | {
      scope: "km";
      workspaceId: string;
      libraryId: string;
    }
  | {
      scope: "regulatory";
      libraryId: string;
    };

/** Lowercase mime without parameters (e.g. charset). */
export function normalizeMime(mimeType: string | null | undefined): string {
  return (mimeType ?? "").toLowerCase().split(";")[0]?.trim() ?? "";
}

function pathOrName(value: string): string {
  try {
    return new URL(value).pathname.toLowerCase();
  } catch {
    return value.toLowerCase();
  }
}

const NON_PDF_EXTENSIONS = new Set([
  ".docx",
  ".doc",
  ".pptx",
  ".ppt",
  ".xlsx",
  ".xls",
  ".html",
  ".htm",
  ".csv",
  ".md",
  ".txt",
]);

const NON_PDF_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/html",
  "text/markdown",
  "text/csv",
  "text/plain",
]);

function hasNonPdfExtension(value: string): boolean {
  const path = pathOrName(value);
  for (const ext of NON_PDF_EXTENSIONS) {
    if (path.endsWith(ext)) return true;
  }
  return false;
}

export function isPdfDocument(
  mimeType: string | null | undefined,
  titleOrUrl: string,
): boolean {
  const mime = normalizeMime(mimeType);
  if (mime === "application/pdf" || mime.endsWith("/pdf")) {
    return true;
  }

  const lower = titleOrUrl.toLowerCase();
  if (lower.endsWith(".pdf")) {
    return true;
  }

  try {
    return new URL(titleOrUrl).pathname.toLowerCase().endsWith(".pdf");
  } catch {
    return lower.includes(".pdf");
  }
}

/** True if mime, title, or download URL indicates a PDF openable in-app. */
export function isPdfOpenable(
  mimeType: string | null | undefined,
  title: string,
  downloadUrl?: string | null,
): boolean {
  if (isPdfDocument(mimeType, title)) {
    return true;
  }
  if (downloadUrl && isPdfDocument(mimeType, downloadUrl)) {
    return true;
  }
  return false;
}

/** True when mime/title/URL clearly identify a non-PDF office or text format. */
export function isKnownNonPdf(
  mimeType: string | null | undefined,
  title: string,
  downloadUrl?: string | null,
): boolean {
  if (isPdfOpenable(mimeType, title, downloadUrl)) {
    return false;
  }
  const mime = normalizeMime(mimeType);
  if (mime && NON_PDF_MIME_TYPES.has(mime)) {
    return true;
  }
  if (hasNonPdfExtension(title)) {
    return true;
  }
  if (downloadUrl && hasNonPdfExtension(downloadUrl)) {
    return true;
  }
  return false;
}

/**
 * Prefer the in-app PDF viewer when the source is a PDF or when type is unknown
 * (matches KM document-list optimism for extensionless titles).
 */
export function shouldOpenInAppPdfViewer(
  mimeType: string | null | undefined,
  title: string,
  downloadUrl?: string | null,
): boolean {
  if (isPdfOpenable(mimeType, title, downloadUrl)) {
    return true;
  }
  return !isKnownNonPdf(mimeType, title, downloadUrl);
}

export function parsePageNumber(
  page: string | number | null | undefined,
): number | null {
  if (page == null || page === "") {
    return null;
  }

  if (typeof page === "number") {
    return Number.isFinite(page) && page > 0 ? Math.floor(page) : null;
  }

  const normalized = page
    .trim()
    .replace(/^(?:page|pg|p)\s*\.?\s*/i, "");
  const match = normalized.match(/^(\d+)/);
  if (!match) {
    return null;
  }

  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** Resolve a citation's display/open page from its page field. */
export function resolveCitationPage(citation: {
  page?: string | number | null;
}): number | undefined {
  return parsePageNumber(citation.page) ?? undefined;
}

export function buildDocumentViewerPath(
  context: DocumentViewerContext,
  documentId: string,
  page?: string,
): string {
  const pageQuery = parsePageNumber(page);
  const query = pageQuery ? `?page=${pageQuery}` : "";

  if (context.scope === "km") {
    return `/rag/workspaces/${context.workspaceId}/libraries/${context.libraryId}/documents/${documentId}/view${query}`;
  }

  return `${REGULATORY_BASE}/libraries/${context.libraryId}/documents/${documentId}/view${query}`;
}

export function buildDocumentsListPath(context: DocumentViewerContext): string {
  if (context.scope === "km") {
    return `/rag/workspaces/${context.workspaceId}/libraries/${context.libraryId}/documents`;
  }

  return `${REGULATORY_BASE}/libraries/${context.libraryId}/documents`;
}

export function buildDocumentOpenUrl(
  downloadUrl: string,
  options: { mimeType: string | null; page?: string | number },
): string {
  const pageNumber = parsePageNumber(options.page);

  if (!pageNumber || !isPdfDocument(options.mimeType, downloadUrl)) {
    return downloadUrl;
  }

  const url = new URL(downloadUrl);
  url.hash = `page=${pageNumber}`;
  return url.toString();
}
